import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    OnDestroy,
    inject,
    signal,
    viewChild,
} from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import {
    ImageCropperComponent,
    ImageTransform,
} from 'ngx-image-cropper';

import { ModalRef } from '~/app/shared/services/modal/modal-ref';
import { ButtonComponent } from '~/app/shared/components/elements/buttons/button/button.component';
import { ButtonWithIconComponent } from '~/app/shared/components/elements/buttons/button-with-icon/button-with-icon.component';
import { H2Component } from '~/app/shared/components/elements/h2/h2.component';
import { IconComponent } from '~/app/shared/components/elements/icon/icon.component';
import { LabelComponent } from '~/app/shared/components/elements/label/label.component';
import { NameInitialsPipe } from '~/app/shared/pipes/name-initials/name-initials.pipe';

export type ProfilePhotoModalData = {
    photo: File | string | null,
    firstName: string,
    lastName: string,
};

export type ProfilePhotoResult =
    | { type: 'updated', file: File }
    | { type: 'deleted' };

type ProfilePhotoView = 'MANAGE' | 'EDIT';

const ACCEPTED_TYPES = 'image/jpeg,image/jpg,image/png';

const DEFAULT_TRANSFORM: ImageTransform = {
    scale: 1,
    rotate: 0,
    flipH: false,
    flipV: false,
    translateH: 0,
    translateV: 0,
};

@Component({
    selector: 'eb-modal-profile-photo',
    templateUrl: './modal-profile-photo.component.html',
    styleUrls: ['./modal-profile-photo.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        TranslocoPipe,
        H2Component,
        LabelComponent,
        IconComponent,
        ButtonComponent,
        ButtonWithIconComponent,
        ImageCropperComponent,
        NameInitialsPipe,
    ],
})
export class ModalProfilePhotoComponent implements OnDestroy {
    private ref = inject<ModalRef<ProfilePhotoResult, ProfilePhotoModalData>>(ModalRef);

    public readonly cropper = viewChild<ImageCropperComponent>('cropper');

    private readonly fileInput = viewChild.required<ElementRef<HTMLInputElement>>('fileInput');

    public readonly firstName = this.ref.data.firstName;

    public readonly lastName = this.ref.data.lastName;

    public readonly acceptedTypes = ACCEPTED_TYPES;

    protected readonly view = signal<ProfilePhotoView>('MANAGE');

    protected readonly hasPhoto = signal(!!this.ref.data.photo);

    /** Aperçu de la vue MANAGE (photo courante), ou null → initiales. */
    protected readonly previewUrl = signal<string | null>(null);

    /** Source éditée : File (photo pické/courant) ou URL distante. */
    protected readonly editFile = signal<File | null>(this.ref.data.photo instanceof File ? this.ref.data.photo : null);

    protected readonly editUrl = signal<string | null>(typeof this.ref.data.photo === 'string' ? this.ref.data.photo : null);

    protected readonly transform = signal<ImageTransform>(DEFAULT_TRANSFORM);

    protected readonly minZoom = 1;

    protected readonly maxZoom = 3;

    protected readonly zoomStep = 0.05;

    protected readonly minRotation = 0;

    protected readonly maxRotation = 360;

    /** Génération du blob recadré en cours (crop asynchrone) → spinner sur « Enregistrer ». */
    protected readonly saving = signal(false);

    private createdObjectUrl: string | null = null;

    constructor() {
        const photo = this.ref.data.photo;
        if (photo instanceof File) {
            this.createdObjectUrl = URL.createObjectURL(photo);
            this.previewUrl.set(this.createdObjectUrl);
        } else if (typeof photo === 'string') {
            this.previewUrl.set(photo);
        }
    }

    public goEdit(): void {
        this.transform.set(DEFAULT_TRANSFORM);
        this.view.set('EDIT');
    }

    public triggerUpdate(): void {
        this.fileInput().nativeElement.click();
    }

    public onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        input.value = ''; // permet de re-sélectionner le même fichier
        if (!file) {
            return;
        }
        this.editFile.set(file);
        this.editUrl.set(null);
        this.transform.set(DEFAULT_TRANSFORM);
        this.view.set('EDIT');
    }

    public remove(): void {
        this.ref.close({ type: 'deleted' });
    }

    public cancelEdit(): void {
        this.view.set('MANAGE');
    }

    public onTransformChange(transform: ImageTransform): void {
        this.transform.set(transform);
    }

    public onZoomChange(scale: number): void {
        this.transform.update((current) => ({ ...current, scale }));
    }

    public onRotationChange(rotate: number): void {
        this.transform.update((current) => ({ ...current, rotate }));
    }

    public rotate(direction: -1 | 1): void {
        this.transform.update((current) => ({
            ...current,
            rotate: (((current.rotate ?? 0) + direction * 90) % 360 + 360) % 360,
        }));
    }

    public flip(): void {
        this.transform.update((current) => ({ ...current, flipH: !current.flipH }));
    }

    public async save(): Promise<void> {
        const event = this.cropper()?.crop('blob');
        if (!event) {
            return;
        }
        this.saving.set(true);
        try {
            const result = await event;
            if (!result.blob) {
                this.saving.set(false);
                return;
            }
            const file = new File([result.blob], 'profile-photo.png', { type: 'image/png' });
            this.ref.close({ type: 'updated', file });
        } catch {
            this.saving.set(false);
        }
    }

    public ngOnDestroy(): void {
        if (this.createdObjectUrl) {
            URL.revokeObjectURL(this.createdObjectUrl);
        }
    }
}
